import { useEffect, useState } from 'react'
import type { OptionsCreationDTO, StatsDTO } from '@whatremains/shared'
import { api, ErreurApi } from '../api'

const NOMS_STATS: Record<keyof StatsDTO, string> = {
  force: 'Force',
  adresse: 'Adresse',
  esprit: 'Esprit',
  vigueur: 'Vigueur',
  fortune: 'Fortune',
}

/** Force et Fortune commencent pareil : les abrégés se posent à la main. */
const ABREGES: Record<keyof StatsDTO, string> = {
  force: 'FOR',
  adresse: 'ADR',
  esprit: 'ESP',
  vigueur: 'VIG',
  fortune: 'FTN',
}

/**
 * La création, en quatre temps.
 *
 * Les deux situations du Penchant n'annoncent aucune conséquence, et n'en
 * afficheront jamais : l'interface ne sait pas ce qu'elles font, parce que le
 * serveur ne le lui dit pas.
 */
export function Creation({ surCree }: { surCree: (id: string) => void }) {
  const [options, setOptions] = useState<OptionsCreationDTO | null>(null)
  const [classe, setClasse] = useState<string | null>(null)
  const [origine, setOrigine] = useState<string | null>(null)
  const [penchant, setPenchant] = useState<(string | null)[]>([])
  const [nom, setNom] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  useEffect(() => {
    void api.optionsCreation().then((o) => {
      setOptions(o)
      setPenchant(o.penchant.map(() => null))
    })
  }, [])

  if (!options) return <div className="cadre"><div className="entete"><p className="tenu">…</p></div></div>

  const complet = classe && origine && penchant.every(Boolean) && nom.trim().length >= 2

  async function creer() {
    if (!classe || !origine) return
    setErreur(null)
    setEnCours(true)
    try {
      const p = await api.creer({
        nom: nom.trim(),
        classe,
        origine,
        penchant: penchant as string[],
      })
      surCree(p.id)
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    } finally {
      setEnCours(false)
    }
  }

  return (
    <div className="cadre">
      <div className="entete">
        <div className="surtitre">Avant tout le reste</div>
        <h1>Qui</h1>
      </div>

      <section>
        <div className="surtitre">01 · Classe</div>
        <div className="grille">
          {options.classes.map((c) => (
            <button
              key={c.code}
              className="choix"
              aria-pressed={classe === c.code}
              onClick={() => setClasse(c.code)}
            >
              <span className="role">{c.role}</span>
              <span className="nom">{c.nom}</span>
              <p style={{ marginTop: 8 }}>{c.fantaisie}</p>
              <p style={{ marginTop: 10, fontFamily: 'var(--mono)', fontSize: 11 }}>
                {(Object.keys(ABREGES) as (keyof StatsDTO)[])
                  .map((s) => `${ABREGES[s]} ${c.statsDepart[s]}`)
                  .join('   ')}
              </p>
            </button>
          ))}
        </div>
      </section>

      {classe && (
        <section>
          <div className="surtitre">02 · Origine</div>
          <div className="grille">
            {options.origines.map((o) => (
              <button
                key={o.code}
                className="choix"
                aria-pressed={origine === o.code}
                onClick={() => setOrigine(o.code)}
              >
                <span className="nom">{o.nom}</span>
                <p style={{ marginTop: 10 }}>{o.texte}</p>
                <p style={{ marginTop: 10, fontFamily: 'var(--mono)', fontSize: 11 }}>
                  {Object.entries(o.bonus)
                    .map(([s, v]) => `${NOMS_STATS[s as keyof StatsDTO]} +${v}`)
                    .join('   ')}
                </p>
              </button>
            ))}
          </div>
        </section>
      )}

      {classe && origine &&
        options.penchant.map((situation, i) => (
          <section key={situation.code}>
            <div className="surtitre">{`0${3 + i}`}</div>
            <h2 style={{ maxWidth: '32ch' }}>{situation.texte}</h2>
            <div className="grille" style={{ maxWidth: 720 }}>
              {situation.options.map((o) => (
                <button
                  key={o.code}
                  className="choix"
                  aria-pressed={penchant[i] === o.code}
                  onClick={() => setPenchant(penchant.map((v, j) => (j === i ? o.code : v)))}
                >
                  {o.texte}
                </button>
              ))}
            </div>
          </section>
        ))}

      {classe && origine && penchant.every(Boolean) && (
        <section>
          <div className="surtitre">{`0${3 + options.penchant.length} · Nom`}</div>
          <label>
            <input
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              maxLength={20}
              placeholder=""
              autoFocus
            />
          </label>
          {erreur && <p className="erreur">{erreur}</p>}
          <button className="action" disabled={!complet || enCours} onClick={() => void creer()}>
            Commencer
          </button>
        </section>
      )}
    </div>
  )
}
